import { formatNumber } from "util/format"

import {
    Box,
    Button,
    Checkbox,
    HStack,
    Icon,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Spacer,
    Text,
    Tooltip,
    useColorModeValue,
} from "@chakra-ui/react"
import { PositionSide } from "@perp/sdk-curie"
import Big from "big.js"
import { useTranslation } from "react-i18next"
import { RiQuestionLine } from "react-icons/ri"
import { CreateLimitOrder } from "sdk-react/limitOrder/useCreateLimitOrder"

import { CreateOrder } from "../../hook/useCreateOrder"
import { usePositionDraftDetailStrings } from "../../hook/usePositionDraftDetailStrings"
import { BuyingPowerSlider } from "./BuyingPowerSlider"

export function CreateOrderSlider() {
    const {
        isOrderTypeLimit,
        isOrderTypeStop,
        side,
        isTxLoading,
        buyingPower,
        buyingPowerRatio,
        updateBuyingPowerRatio,
    } = CreateOrder.useContainer()
    const {
        isReduceOnly,
        setIsReduceOnly,
        reduceOnlyPositionSizeRatio,
        hasOppositePosition,
        updatePositionSizeForReduceOnly,
    } = CreateLimitOrder.useContainer()
    const { buyingPowerStr } = usePositionDraftDetailStrings()
    const isShort = side === PositionSide.SHORT
    const isInvalidBuyingPower = !buyingPower || buyingPower.lte(0)

    const { t } = useTranslation()
    const subText = useColorModeValue("gray.500", "gray.400")

    return (
        <>
            {isReduceOnly && (isOrderTypeLimit || isOrderTypeStop) ? (
                <>
                    <Tooltip label="Reduce or close your existing position">
                        <Text variant="label">
                            {t("general.position_size")}&nbsp;
                            <Icon position="relative" top="4px" opacity="0.6" boxSize="16px" as={RiQuestionLine} />
                        </Text>
                    </Tooltip>
                    <Box borderRadius="2xl" bg={"blackAlpha.500"} p={4} py={3} w="100%">
                        <HStack h="34px" spacing={4}>
                            <Slider
                                pl={1}
                                aria-label="position-size-slider-reduce-only"
                                value={reduceOnlyPositionSizeRatio}
                                onChange={updatePositionSizeForReduceOnly}
                                min={0}
                                max={100}
                                step={5}
                                focusThumbOnChange={false}
                            >
                                <SliderTrack>
                                    <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </HStack>
                        <HStack align="stretch" w="100%" mt={2}>
                            <Text whiteSpace="nowrap" fontSize="xs" color={subText}>
                                Reduce {reduceOnlyPositionSizeRatio.toFixed(1)}% of the current position
                            </Text>
                        </HStack>
                    </Box>
                </>
            ) : (
                <>
                    <Tooltip label="Max buying power ≈ Free Collateral × 10">
                        <HStack spacing={0}>
                            <Text variant="label">
                                {t("general.buying_power")}&nbsp;
                                <Icon position="relative" top="4px" opacity="0.6" boxSize="16px" as={RiQuestionLine} />
                            </Text>
                            <Spacer />
                            <Text isTruncated whiteSpace="nowrap" fontSize="xs" color={subText}>
                                ≈ ${buyingPowerStr}
                            </Text>
                        </HStack>
                    </Tooltip>
                    <Box borderRadius="2xl" bg={"blackAlpha.500"} p={4} py={3} w="100%">
                        <BuyingPowerSlider
                            pl={1}
                            isShort={isShort}
                            isDisabled={isInvalidBuyingPower || isTxLoading}
                            value={buyingPowerRatio}
                            onSlideChange={updateBuyingPowerRatio}
                        />

                        <HStack align="stretch" w="100%" mt={2}>
                            <Text whiteSpace="nowrap" fontSize="xs" color={subText}>
                                {formatNumber(Big(buyingPowerRatio * 100), 0)}% of BP
                            </Text>
                            <Spacer />
                            <Text whiteSpace="nowrap" fontSize="xs" color={subText}></Text>
                        </HStack>
                    </Box>
                </>
            )}

            {(isOrderTypeLimit || isOrderTypeStop) && (
                <HStack w="full">
                    <Tooltip
                        isDisabled={!!hasOppositePosition}
                        shouldWrapChildren
                        label="No existing position to reduce"
                    >
                        <Checkbox
                            isDisabled={!hasOppositePosition}
                            isChecked={isReduceOnly}
                            size={"sm"}
                            onChange={(event: { target: { checked: boolean } }) => {
                                setIsReduceOnly(event.target.checked)
                            }}
                        >
                            {t("general.reduce_only")}
                        </Checkbox>
                    </Tooltip>
                    <Spacer />
                    <Button
                        isDisabled={!hasOppositePosition}
                        colorScheme="gray"
                        size="xs"
                        onClick={() => {
                            setIsReduceOnly(true)
                            updatePositionSizeForReduceOnly(100)
                        }}
                    >
                        Close 100%
                    </Button>
                </HStack>
            )}
        </>
    )
}
